import React, { useEffect, useState } from 'react'
import { Container } from '../../../../components'
import { CircularProgress, useTheme } from '@material-ui/core'
import { InsertDriveFile } from '@material-ui/icons'
import { axiosInstance } from '../../../../helpers'
import { Document } from '../../../../models'

const IMAGE_EXTENSIONS = /\.(png|jpe?g|gif|webp|bmp)$/i

interface Props {
    vendorId: number
    slot: string
    document: Document
}

const fileBasename = (name?: string) => {
    if (!name) {
        return 'Document'
    }
    const parts = name.split('/')
    return parts[parts.length - 1] || 'Document'
}

const isImageDocument = (name?: string) => {
    return IMAGE_EXTENSIONS.test(name || '')
}

export const DocumentPreviewCard = (props: Props) => {
    const { vendorId, slot, document } = props
    const theme = useTheme()
    const label = fileBasename(document.name)
    const [previewSrc, setPreviewSrc] = useState<string | null>(
        isImageDocument(document.name) && document.url ? document.url : null,
    )
    const [loading, setLoading] = useState(!isImageDocument(document.name))

    useEffect(() => {
        if (isImageDocument(document.name) && document.url) {
            setPreviewSrc(document.url)
            setLoading(false)
            return
        }

        let objectUrl: string | undefined
        let cancelled = false
        setLoading(true)
        axiosInstance
            .get(
                `company/national_vendor_list/${vendorId}/get_vendor_document_preview/?slot=${slot}`,
                { responseType: 'blob' },
            )
            .then((res) => {
                objectUrl = URL.createObjectURL(res.data)
                if (cancelled) {
                    URL.revokeObjectURL(objectUrl)
                    return
                }
                setPreviewSrc(objectUrl)
            })
            .catch(() => {
                if (!cancelled) {
                    setPreviewSrc(null)
                }
            })
            .finally(() => {
                if (!cancelled) {
                    setLoading(false)
                }
            })

        return () => {
            cancelled = true
            if (objectUrl) {
                URL.revokeObjectURL(objectUrl)
            }
        }
    }, [vendorId, slot, document.name, document.url])

    const openDocument = () => {
        if (document.url) {
            window.open(document.url)
        }
    }

    return (
        <Container
            direction="column"
            style={{
                flex: '0 0 96px',
                width: 96,
                cursor: document.url ? 'pointer' : 'default',
            }}
            onClick={openDocument}
        >
            <div
                title={label}
                style={{
                    position: 'relative',
                    width: 96,
                    aspectRatio: '8.5 / 11',
                    backgroundColor: '#fafafa',
                    border: `1px solid ${theme.palette.grey[300]}`,
                    borderRadius: 6,
                    overflow: 'hidden',
                    boxShadow: '0 1px 2px rgba(0, 0, 0, 0.06)',
                }}
            >
                {loading ? (
                    <Container
                        alignItems="center"
                        justifyContent="center"
                        style={{
                            position: 'absolute',
                            top: 0,
                            left: 0,
                            right: 0,
                            bottom: 0,
                        }}
                    >
                        <CircularProgress size={16} />
                    </Container>
                ) : previewSrc ? (
                    <img
                        src={previewSrc}
                        alt={label}
                        style={{
                            position: 'absolute',
                            top: 0,
                            left: 0,
                            width: '100%',
                            height: '100%',
                            objectFit: 'contain',
                            display: 'block',
                        }}
                    />
                ) : (
                    <Container
                        alignItems="center"
                        justifyContent="center"
                        style={{
                            position: 'absolute',
                            top: 0,
                            left: 0,
                            right: 0,
                            bottom: 0,
                        }}
                    >
                        <InsertDriveFile
                            style={{
                                fontSize: 24,
                                color: theme.palette.grey[400],
                            }}
                        />
                    </Container>
                )}
            </div>
        </Container>
    )
}
